import { ThemeOptions } from '@mui/material/styles';
import { PaletteOptions } from '@mui/material/styles/createPalette';

export const borderRadiusValues = {
  big: 1.6,
  medium: 1,
  small: 0.4,
  xSmall: 0.2,
};
export const DEFAULT_ELEVATION = 4;

declare module '@mui/material/styles' {
  interface Palette {
    custom: {
      CODD: string;
      scoreDigit: string;
      forecastItem: string;
      lightText: string;
    };
    customBackgrounds: {
      elevation4: string;
      elevation5: string;
      elevation6: string;
      contrast: string;
    };
  }
  interface PaletteOptions {
    custom?: {
      CODD: string;
      scoreDigit: string;
      forecastItem: string;
      lightText: string;
    };
    customBackgrounds?: {
      elevation4: string;
      elevation5: string;
      elevation6: string;
      contrast: string;
    };
  }

  interface BreakpointOverrides {
    xs: false; // removes the `xs` breakpoint
    sm: false;
    md: false;
    lg: false;
    xl: false;
    mobile: true; // adds the `mobile` breakpoint
    tablet: true;
    laptop: true;
    desktop_small: true;
    desktop_middle: true;
    desktop_large: true;
  }

  // Типография
  interface TypographyVariants {
    f10: React.CSSProperties;
    f8: React.CSSProperties;
    f12: React.CSSProperties;
    f12b: React.CSSProperties;
    f14: React.CSSProperties;
    f14b: React.CSSProperties;
    f16b: React.CSSProperties;
    f24: React.CSSProperties;
    f26: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    f10: React.CSSProperties;
    f8: React.CSSProperties;
    f12: React.CSSProperties;
    f12b: React.CSSProperties;
    f14: React.CSSProperties;
    f14b: React.CSSProperties;
    f16b: React.CSSProperties;
    f24: React.CSSProperties;
    f26: React.CSSProperties;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    f10: true;
    f8: true;
    f12: true;
    f12b: true;
    f14: true;
    f14b: true;
    f16b: true;
    f24: true;
    f26: true;
  }
}

export const defaultPalette: PaletteOptions = {
  success: {
    main: '#75B348',
  },
  warning: {
    main: '#FFC914',
  },
  error: {
    main: '#DC5648',
  },
} as const;

export const lightThemeParams: ThemeOptions = {
  palette: {
    primary: {
      main: '#28B67E',
      contrastText: '#fff',
    },
    secondary: {
      main: '#3A86FF',
    },
    custom: {
      CODD: '#83CC24',
      scoreDigit: '#343449',
      forecastItem: '#0F0F0F',
      lightText: '#EBEBEB',
    },
    customBackgrounds: {
      elevation4: '#343449',
      elevation5: '#343449',
      elevation6: '#343449',
      contrast: 'white',
    },
    ...defaultPalette,
  },
};
export const darkThemeParams: ThemeOptions = {
  palette: {
    mode: 'dark',
    primary: {
      main: '#28B67E',
      contrastText: '#1F2933',
    },
    secondary: {
      main: '#3A86FF',
    },
    background: {
      default: '#343449',
      paper: '#343449',
    },
    text: {
      primary: '#ffffff',
      secondary: '#878787',
    },
    success: {
      main: '#83CC24',
    },
    error: {
      main: '#FC4A1E',
    },
    warning: {
      main: '#FFC507',
    },
    customBackgrounds: {
      elevation4: '#343449',
      elevation5: '#4B4B5C',
      elevation6: 'white',
      contrast: 'white',
    },
    custom: {
      CODD: '#83CC24',
      scoreDigit: '#343449',
      forecastItem: '#0F0F0F',
      lightText: '#EBEBEBA7',
    },
    ...defaultPalette,
  },
};

export const commonThemeParams: ThemeOptions = {
  breakpoints: {
    values: {
      mobile: 0,
      tablet: 640,
      laptop: 980,
      desktop_small: 1024,
      desktop_middle: 1280,
      desktop_large: 1920,
    },
  },
  spacing: 4,
  shape: {
    borderRadius: 10,
  },
  typography: {
    fontFamily: 'MoscowSans',
    button: {
      fontSize: '0.75rem',
      lineHeight: 'normal',
    },
    f8: {
      fontSize: '0.5rem',
      lineHeight: 'normal',
    },
    f10: {
      fontSize: '0.625rem',
      lineHeight: 'normal',
    },
    f12: {
      fontSize: '0.75rem',
      lineHeight: 'normal',
    },
    f12b: {
      fontSize: '0.75rem',
      lineHeight: 'normal',
      fontWeight: 800,
    },
    f14: {
      fontSize: '0.875rem',
      lineHeight: 'normal',
    },
    f14b: {
      fontSize: '0.875rem',
      lineHeight: 'normal',
      fontWeight: 600,
    },
    f16b: {
      fontSize: '1rem',
      lineHeight: 'normal',
      fontWeight: 800,
    },
    f24: {
      fontSize: '1.5rem',
      lineHeight: 'normal',
    },
    f26: {
      fontSize: '1.625rem',
      lineHeight: 'normal',
    },
  },
  components: {
    MuiPaper: {
      defaultProps: {
        elevation: DEFAULT_ELEVATION,
      },
      styleOverrides: {
        // Вырубаем стандартное поведение, чтобы определить точно в соответствии с макетом
        elevation: {
          boxShadow: 'none !important',
        },
        elevation6: ({ theme }) => {
          return {
            background: theme.palette.customBackgrounds.elevation6,
          };
        },
        elevation5: ({ theme }) => {
          return {
            background: theme.palette.customBackgrounds.elevation5,
          };
        },
        elevation4: ({ theme }) => {
          return {
            background: theme.palette.customBackgrounds.elevation4,
          };
        },
      },
    },
    MuiAppBar: {
      defaultProps: {
        elevation: DEFAULT_ELEVATION,
      },
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'elipsis',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: ({ theme }) => {
          return {
            gap: `${theme.spacing(1)}`,
          };
        },
      },
    },
  },
};

export const defaultThemeParams: ThemeOptions = {
  ...lightThemeParams,
  ...commonThemeParams,
};
